import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Button,
  Link,
  Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudDone as CloudDoneIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'integration_banner_dismissed';

const IntegrationBanner = () => {
  const { user, subscriptionStatus } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasHubspot, setHasHubspot] = useState(false);
  const [hasSalesforce, setHasSalesforce] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed
    const dismissedState = localStorage.getItem(STORAGE_KEY);
    if (dismissedState === 'true') {
      setDismissed(true);
      setShowBanner(false);
      setLoading(false);
      return;
    }

    // Only check add-on status if user is logged in and has active subscription
    // Check for active subscription status (active, trial, or cancelled but still active)
    const hasActiveSubscription = subscriptionStatus && (
      subscriptionStatus.status === 'active' || 
      subscriptionStatus.status === 'trial' ||
      (subscriptionStatus.status === 'cancelled' && subscriptionStatus.is_cancelled_but_active) ||
      subscriptionStatus.subscription_active
    );
    
    if (!user || !hasActiveSubscription) {
      setLoading(false);
      return;
    }

    checkAddOnStatus();
  }, [user, subscriptionStatus]);

  const checkAddOnStatus = async () => {
    try {
      setLoading(true);
      const response = await api.getAddOnStatus();
      
      if (response.success && response.data) {
        setHasHubspot(response.data.has_hubspot || false);
        setHasSalesforce(response.data.has_salesforce || false);
        
        // Show banner only if neither add-on is active
        setShowBanner(!response.data.has_hubspot && !response.data.has_salesforce);
      } else {
        setShowBanner(false);
      }
    } catch (error) {
      logger.error('Error checking add-on status:', error);
      setShowBanner(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleViewAddOns = () => {
    // Dispatch custom event to open user settings dialog with subscription tab and scroll to add-ons
    window.dispatchEvent(new CustomEvent('openUserSettings', { 
      detail: { 
        tab: 2, // Tab 2 is the Subscription tab
        scrollToAddOns: true // Flag to scroll to add-ons section
      }
    }));
  };

  // Check for active subscription status
  const hasActiveSubscription = subscriptionStatus && (
    subscriptionStatus.status === 'active' || 
    subscriptionStatus.status === 'trial' ||
    (subscriptionStatus.status === 'cancelled' && subscriptionStatus.is_cancelled_but_active) ||
    subscriptionStatus.subscription_active
  );
  
  // Don't render if loading, dismissed, or user doesn't have active subscription
  if (loading || dismissed || !showBanner || !user || !hasActiveSubscription) {
    return null;
  }

  return (
    <Collapse in={showBanner}>
      <Box sx={{ mb: 2 }}>
        <Alert
          severity="info"
          icon={<InfoIcon />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleViewAddOns}
                sx={{ textTransform: 'none' }}
              >
                View Add-Ons
              </Button>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          }
          sx={{
            borderRadius: 1,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
            Enhance Your Workflow with CRM Integrations
          </AlertTitle>
          <Box component="span" sx={{ fontSize: '0.875rem' }}>
            Connect your Salesforce or HubSpot CRM to sync contacts, enrich data, and streamline your sales process.{' '}
            <Link
              component="button"
              onClick={handleViewAddOns}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Learn more
            </Link>
          </Box>
        </Alert>
      </Box>
    </Collapse>
  );
};

export default IntegrationBanner;

